import { Injectable } from '@angular/core';
import { results } from 'src/app/common/models/common.model';

@Injectable({
    providedIn: 'root'
})
export class IndexedDBService {
    private dbName = 'KAP_Dashboard_DB';
    private storeName = 'client_study_cache';
    private dbVersion = 1;
    private db: IDBDatabase | null = null;

    constructor() {
        this.initDB();
    }

    private initDB(): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            if (this.db) {
                resolve(this.db);
                return;
            }

            if (!window.indexedDB) {
                reject('IndexedDB not supported');
                return;
            }

            const request = indexedDB.open(this.dbName, this.dbVersion);

            request.onerror = (event) => {
                console.error('IndexedDB error:', event);
                reject('Error opening IndexedDB');
            };

            request.onsuccess = (event: any) => {
                this.db = event.target.result;
                resolve(this.db!);
            };

            request.onupgradeneeded = (event: any) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    const objectStore = db.createObjectStore(this.storeName, { keyPath: 'key' });
                    objectStore.createIndex('timestamp', 'timestamp', { unique: false });
                }
            };
        });
    }

    private generateKey(client: string, study: string, isBrandAnalysis: boolean): string {
        return `${client}_${study}_${isBrandAnalysis}`;
    }

    /**
     * Retrieves data from IndexedDB if it exists and hasn't expired (24h)
     */
    async getClientStudyData(client: string, study: string, isBrandAnalysis: boolean): Promise<results | null> {
        try {
            const db = await this.initDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const key = this.generateKey(client, study, isBrandAnalysis);
                const request = store.get(key);

                request.onsuccess = (event: any) => {
                    const record = event.target.result;
                    if (record) {
                        // Check expiry (24 hours = 86400000 ms)
                        const now = new Date().getTime();
                        if (now - record.timestamp < 86400000) {
                            resolve(record.data);
                        } else {
                            // Expired
                            this.deleteRecord(key); // Clear silently
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                };

                request.onerror = (event) => {
                    console.error('Error fetching from IndexedDB:', event);
                    resolve(null); // Fail safe
                };
            });
        } catch (error) {
            console.error('Error in getClientStudyData:', error);
            return null;
        }
    }

    /**
     * Caches the client study data
     */
    async cacheClientStudyData(client: string, study: string, isBrandAnalysis: boolean, data: results): Promise<void> {
        try {
            const db = await this.initDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const key = this.generateKey(client, study, isBrandAnalysis);

                const record = {
                    key: key,
                    data: data,
                    timestamp: new Date().getTime()
                };

                const request = store.put(record);

                request.onsuccess = () => resolve();
                request.onerror = (event) => {
                    console.error('Error writing to IndexedDB:', event);
                    reject(event);
                }
            });
        } catch (error) {
            console.error('Error caching data:', error);
        }
    }

    /**
     * Clears records older than 24 hours
     */
    async clearExpiredCache(): Promise<void> {
        try {
            const db = await this.initDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const index = store.index('timestamp');
                const keyRange = IDBKeyRange.upperBound(new Date().getTime() - 86400000); // Older than 24h

                const request = index.openCursor(keyRange);

                request.onsuccess = (event: any) => {
                    const cursor = event.target.result;
                    if (cursor) {
                        cursor.delete();
                        cursor.continue();
                    } else {
                        resolve();
                    }
                };

                request.onerror = (event) => reject(event);
            });
        } catch (e) {
            console.error('Error clearing expired cache:', e);
        }
    }

    private deleteRecord(key: string) {
        if (!this.db) return;
        try {
            const transaction = this.db.transaction([this.storeName], 'readwrite');
            const store = transaction.objectStore(this.storeName);
            store.delete(key);
        } catch (e) {
            console.error('Error deleting record:', e);
        }
    }

    /**
     * Clears all client study cache
     */
    async clearClientStudyCache(): Promise<void> {
        try {
            const db = await this.initDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readwrite');
                const store = transaction.objectStore(this.storeName);
                const request = store.clear();
                request.onsuccess = () => resolve();
                request.onerror = (e) => reject(e);
            });
        } catch (e) { console.error('Error clearing cache:', e); }
    }

    async getStats(): Promise<any> {
        try {
            const db = await this.initDB();
            return new Promise((resolve, reject) => {
                const transaction = db.transaction([this.storeName], 'readonly');
                const store = transaction.objectStore(this.storeName);
                const request = store.count();
                request.onsuccess = () => resolve({ items: request.result });
                request.onerror = () => resolve({ items: 0 });
            });
        } catch (e) { return { items: 0 }; }
    }
}
